import { createContext, useContext } from "react";
import type { DriveItem } from "@/api/driveApi";

/**
 * Every item operation in Drive, shared by the list, grid, menus,
 * toolbars, details pane and preview. Implemented once in
 * DriveUiProvider so feedback (toasts, undo, dialogs) is consistent.
 */
export interface DriveActions {
    /** Folder → navigate; Office file → open in app; other file → preview. */
    open: (item: DriveItem, list?: DriveItem[]) => void;
    /** Full-screen preview; ←/→ step through `list`'s files. */
    preview: (item: DriveItem, list?: DriveItem[]) => void;
    /**
     * Office file → Docs / Sheets / Slides; PDF → the PDF editor. `target` is
     * a tab opened earlier (during the click) to navigate instead of a new one.
     */
    openInApp: (item: DriveItem, target?: Window | null) => void;
    download: (items: DriveItem[]) => Promise<void>;
    /** Rename via dialog. */
    rename: (item: DriveItem) => Promise<void>;
    /** Rename directly (inline F2). Resolves false on failure. */
    renameTo: (item: DriveItem, name: string) => Promise<boolean>;
    copy: (items: DriveItem[]) => Promise<void>;
    /** Copy into a folder chosen in the picker. */
    copyTo: (items: DriveItem[]) => Promise<void>;
    /** Move via the folder picker. */
    move: (items: DriveItem[]) => Promise<void>;
    /** Move to a known folder (drag-and-drop), with Undo. */
    moveTo: (items: DriveItem[], targetId: string | null) => Promise<void>;
    /** Stars everything, or unstars when all are already starred. */
    toggleStar: (items: DriveItem[]) => void;
    share: (item: DriveItem) => void;
    /** Copies the /open/<id> link (works for anyone with access). */
    copyLink: (item: DriveItem) => Promise<void>;
    history: (item: DriveItem) => void;
    unzip: (item: DriveItem) => Promise<void>;
    protect: (item: DriveItem) => void;
    /** "Request files" — manage upload links for one of the user's folders. */
    requestFiles: (folder: DriveItem) => void;
    /** Move to bin, with Undo. */
    trash: (items: DriveItem[]) => Promise<void>;
    restore: (items: DriveItem[]) => Promise<void>;
    /** Permanent delete (confirm dialog). */
    deleteForever: (items: DriveItem[]) => Promise<void>;
    emptyBin: (count: number) => Promise<void>;
    /** Zip into Drive (name prompt). */
    zip: (items: DriveItem[], parentId: string | null) => Promise<void>;
    newFolder: (parentId: string | null) => Promise<void>;
    /** "Start something new" — the template gallery, creating in `parentId`. */
    openTemplates: (parentId: string | null) => void;
    /**
     * Copies a template into `parentId`, toasts and opens the copy in its app.
     * Call straight from a click: the editor tab is opened before the request.
     */
    createFromTemplate: (templateId: string, parentId: string | null) => Promise<DriveItem | null>;
    showShortcuts: () => void;
}

export const DriveActionsContext = createContext<DriveActions | null>(null);

export function useDriveActions(): DriveActions {
    const ctx = useContext(DriveActionsContext);
    if (!ctx) throw new Error("useDriveActions must be used inside <DriveUiProvider>");
    return ctx;
}
